import { useEffect, useState } from "react";
import { api, DIMENSIONS } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { BusinessCase } from "../components/BusinessCase";
import { BenefitsTracking } from "../components/BenefitsTracking";
import { toast } from "sonner";
import { Sparkle, SealCheck } from "@phosphor-icons/react";

export default function TargetModel() {
  const [scenarios, setScenarios] = useState([]);
  const [models, setModels] = useState([]);
  const [generating, setGenerating] = useState(false);

  const load = () => Promise.all([api.get("/scenarios"), api.get("/target-models")]).then(([s, m]) => { setScenarios(s.data); setModels(m.data); });
  useEffect(() => { load(); }, []);

  const approve = async (key) => {
    const { data } = await api.post("/scenarios/approve", { scenario_key: key });
    setScenarios(data);
    toast.success("Scenario approved with named human approval — recorded in audit trail");
  };

  const generate = async (key) => {
    setGenerating(true);
    toast.info("Copilot is drafting the integrated operating model — this takes 1–2 minutes…");
    try {
      const prevCount = models.length;
      const prevLatest = models[0]?.generated_at;
      await api.post("/copilot/generate-target-model", { scenario_key: key });
      for (let i = 0; i < 40; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        const { data } = await api.get("/target-models");
        if (data.length > prevCount || (data[0] && data[0].generated_at !== prevLatest)) {
          setModels(data);
          const { data: sc } = await api.get("/scenarios");
          setScenarios(sc);
          toast.success("Draft generated — requires named human approval before publication");
          setGenerating(false);
          return;
        }
      }
      toast.error("Generation is taking longer than expected. Refresh this page shortly.");
    } catch (e) {
      toast.error("Generation failed. Please try again.");
    }
    setGenerating(false);
  };

  const approveModel = async (id) => {
    await api.post("/target-models/approve", { model_id: id });
    await load();
    toast.success("Recommended integrated operating model approved");
  };

  const approved = scenarios.find((s) => s.approved);
  const activeKey = approved?.key || scenarios.find((s) => s.recommended)?.key || "balanced";
  const latest = models[0];

  const meter = (label, v, invert) => (
    <div className="text-[11px]">
      <div className="flex justify-between text-black/55"><span>{label}</span><span className="font-mono">{v}</span></div>
      <div className="h-1.5 bg-black/8 rounded-full mt-0.5 overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${v}%`, background: invert ? (v > 60 ? "#EF4444" : v > 35 ? "#F59E0B" : "#22C55E") : "#D4AF37" }} />
      </div>
    </div>
  );

  return (
    <div className="canvas-grain min-h-screen pb-16" data-testid="target-model-page">
      <PageHeader overline="Recommended Integrated Operating Model" title="Future-State Options"
        subtitle="Three scenarios with value, cost, complexity, risk and workforce impact. One is recommended — executives may approve a different path. AI drafts are never silently published." />
      <div className="px-8 grid grid-cols-1 lg:grid-cols-3 gap-5">
        {scenarios.map((s) => (
          <div key={s.key} data-testid={`scenario-card-${s.key}`}
            className={`bg-white border rounded-sm p-5 flex flex-col ${s.approved ? "border-[#D4AF37] ring-1 ring-[#D4AF37]" : s.recommended ? "border-black/30" : "border-black/10"}`}>
            <div className="flex items-start justify-between">
              <h3 className="font-display font-bold text-lg tracking-tight">{s.name}</h3>
              {s.approved ? <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider bg-[#D4AF37] text-black px-2 py-1 rounded-sm font-semibold"><SealCheck size={12} weight="fill" />Approved</span>
                : s.recommended ? <span className="text-[10px] uppercase tracking-wider bg-[#0A0A0A] text-[#D4AF37] px-2 py-1 rounded-sm font-semibold">Recommended</span> : null}
            </div>
            <p className="text-[13px] text-black/60 mt-2 flex-1">{s.description}</p>
            <div className="space-y-2 mt-4">
              {meter("Value", s.value)}
              {meter("Cost", s.cost, true)}
              {meter("Complexity", s.complexity, true)}
              {meter("Risk", s.risk, true)}
            </div>
            <p className="text-[11px] text-black/55 mt-3"><b>Workforce impact:</b> {s.workforce_impact}</p>
            <p className="text-[11px] text-black/55"><b>Time to value:</b> {s.time_to_value}</p>
            <p className="text-[11px] text-black/55"><b>Dependencies:</b> {s.dependencies.join("; ")}</p>
            {s.approved && s.approved_by && <p className="text-[11px] text-[#D4AF37] mt-1">Approved by {s.approved_by}</p>}
            <div className="flex gap-2 mt-4">
              {!s.approved && (
                <button onClick={() => approve(s.key)} data-testid={`approve-scenario-${s.key}`}
                  className="flex-1 text-xs border border-black/25 rounded-sm py-2 font-semibold hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">Approve this path</button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="px-8"><BusinessCase /></div>
      <div className="px-8"><BenefitsTracking /></div>

      <div className="px-8 mt-8">
        <div className="bg-[#0A0A0A] rounded-sm p-6 text-white">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] font-semibold">AI Copilot — Claude Sonnet 4.6</p>
              <h2 className="font-display text-2xl font-bold tracking-tight">Integrated Operating Model Draft</h2>
              <p className="text-white/60 text-[13px] mt-1 max-w-2xl">Generated from workspace evidence for the {activeKey} scenario. Sources and assumptions are labelled; a named human must approve before it is treated as the recommendation.</p>
            </div>
            <button onClick={() => generate(activeKey)} disabled={generating} data-testid="generate-target-model-btn"
              className="flex items-center gap-2 bg-[#D4AF37] text-[#0A0A0A] text-sm font-semibold px-4 py-2.5 rounded-sm hover:bg-[#e0c258] transition-colors disabled:opacity-50">
              <Sparkle size={15} weight="fill" /> {generating ? "Generating…" : latest ? "Regenerate draft" : "Generate draft"}
            </button>
          </div>

          {latest && (
            <div className="mt-6 fade-up" data-testid="generated-model">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-[10px] uppercase tracking-wider bg-purple-500/20 text-purple-300 border border-purple-400/40 px-2 py-1 rounded-sm">AI-generated · confidence {latest.model.confidence}</span>
                {latest.approved
                  ? <span className="text-[10px] uppercase tracking-wider bg-[#D4AF37] text-black px-2 py-1 rounded-sm font-semibold">Approved by {latest.approved_by}</span>
                  : <button onClick={() => approveModel(latest.id)} data-testid="approve-target-model-btn"
                      className="text-[11px] border border-[#D4AF37] text-[#D4AF37] px-3 py-1 rounded-sm hover:bg-[#D4AF37]/10 transition-colors">Approve as recommendation (named approval)</button>}
              </div>
              <h3 className="font-display text-xl font-bold tracking-tight mt-4 text-[#D4AF37]">{latest.model.headline}</h3>
              <p className="text-white/75 text-[13px] mt-2 max-w-3xl">{latest.model.rationale}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-5">
                {DIMENSIONS.map((d) => (
                  <div key={d.key} className="border border-white/12 rounded-sm p-4" data-testid={`model-dimension-${d.key}`}>
                    <p className="font-display font-bold text-sm tracking-tight" style={{ color: d.color }}>{d.label}</p>
                    <ul className="mt-2 space-y-1.5">
                      {(latest.model.dimensions?.[d.key] || []).map((item, i) => (
                        <li key={i} className="text-[12px] text-white/75 flex gap-2"><span style={{ color: d.color }}>—</span>{item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div className="border border-white/12 rounded-sm p-4">
                  <p className="text-[10px] uppercase tracking-[0.15em] text-[#D4AF37] mb-2">Critical dependencies</p>
                  {(latest.model.critical_dependencies || []).map((x, i) => <p key={i} className="text-[12px] text-white/75">— {x}</p>)}
                </div>
                <div className="border border-white/12 rounded-sm p-4">
                  <p className="text-[10px] uppercase tracking-[0.15em] text-[#D4AF37] mb-2">First 90 days</p>
                  {(latest.model.first_90_days || []).map((x, i) => <p key={i} className="text-[12px] text-white/75">— {x}</p>)}
                </div>
                <div className="border border-white/12 rounded-sm p-4">
                  <p className="text-[10px] uppercase tracking-[0.15em] text-amber-400 mb-2">Assumptions (need validation)</p>
                  {(latest.model.assumptions || []).map((x, i) => <p key={i} className="text-[12px] text-white/75">— {x}</p>)}
                </div>
              </div>
            </div>
          )}
          {!latest && <p className="text-white/40 text-sm mt-6">No draft yet — generate one from your approved scenario. The Copilot only uses workspace evidence.</p>}
        </div>
      </div>
    </div>
  );
}
